import { useFileUpload } from "@msuite/ui/file-upload";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type ProjectInvoiceSchema, projectInvoiceSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { ProjectInvoiceForm } from "@/forms/project-invoice-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditProjectInvoiceModalProps {
	isOpen: boolean;
	onClose: () => void;
	projectId: string;
	invoiceId: string;
	onComplete: () => Promise<void>;
}

export function EditProjectInvoiceModal({
	isOpen,
	onClose,
	projectId,
	invoiceId,
	onComplete,
}: EditProjectInvoiceModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.projects[":projectId"].invoices[":invoiceId"].$get({
			param: {
				projectId,
				invoiceId,
			},
		});
		const data = await responseHandler(response);
		return data.invoice as ProjectInvoiceSchema;
	});
	const methods = useForm({
		resolver: resolver(projectInvoiceSchema),
		defaultValues,
	});
	const { uploadFn } = useUpload();
	const controller = useFileUpload({
		defaultFiles: async () => {
			const response = await client.projects[":projectId"].invoices[":invoiceId"].files.$get({
				param: {
					projectId,
					invoiceId,
				},
			});
			const data = await responseHandler(response);
			return data.files;
		},
		uploadFn,
	});

	/** Functions */
	async function handleOnSubmit(json: ProjectInvoiceSchema) {
		try {
			const uploadResults = await controller.handleOnFileUpload();
			await client.projects[":projectId"].invoice[":invoiceId"].$patch({
				json: {
					...json,
					uploadResults,
				},
				param: {
					projectId,
					invoiceId,
				},
			});
			await onComplete();
			onClose();
		} catch (error) {
			console.error(error);
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("invoices.edit_invoice")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ProjectInvoiceForm controller={controller} />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
